import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBinoculars, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { BIZDEV_NAV } from './nav';

const Competitors: React.FC = () => {
    const { api } = useInternalApi();
    const [notes, setNotes] = useState<any[]>([]);
    const [names, setNames] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ competitor: '', observation: '', category: '', source: '', observed_on: '' });

    useEffect(() => { document.title = 'Competitive Intel - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('bizdev-api/competitors/');
            if (r.data?.status === 'success') {
                setNotes(r.data.data.notes || []);
                setNames(r.data.data.competitors || []);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!form.competitor.trim() || !form.observation.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Competitor and observation are both required.' });
            return;
        }
        try {
            await api.post('bizdev-api/competitors/', form);
            setShowAdd(false);
            setForm({ competitor: '', observation: '', category: '', source: '', observed_on: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not record observation.' });
        }
    };

    const remove = async (n: any) => {
        const c = await Swal.fire({ title: 'Remove this observation?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!c.isConfirmed) return;
        try { await api.delete(`bizdev-api/competitors/${n.id}/`); load(); }
        catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Could not remove.' }); }
    };

    const shown = filter ? notes.filter(n => n.competitor === filter) : notes;

    return (
        <InternalShell activeDashboard="business_development" subNav={BIZDEV_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faBinoculars} className="me-2 text-primary" />Competitive Intelligence</h2>
                    <p className="text-muted mb-0">What was observed, when, and where it came from</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Record Observation</button>
            </div>

            <div className="alert alert-secondary small">
                Every entry carries a source so a claim can be traced back rather than becoming folklore. If you cannot cite where something came from, note that too.
            </div>

            <select className="form-select mb-3" style={{ maxWidth: 260 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="">All competitors</option>
                {names.map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : shown.length === 0 ? (
                        <div className="text-center py-5 text-muted">No observations recorded.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Competitor</th><th>Observation</th><th>Category</th><th>Source</th><th>Observed</th><th></th></tr></thead>
                            <tbody>
                                {shown.map(n => (
                                    <tr key={n.id}>
                                        <td><strong>{n.competitor}</strong></td>
                                        <td className="small">{n.observation}</td>
                                        <td className="small">{n.category || '—'}</td>
                                        <td className="small">{n.source || <span className="text-warning">unsourced</span>}</td>
                                        <td className="small">{n.observed_on}</td>
                                        <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => remove(n)}><FontAwesomeIcon icon={faTrash} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Record Observation</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Competitor</label>
                                    <input className="form-control" list="competitor-names" value={form.competitor}
                                        onChange={(e) => setForm(f => ({ ...f, competitor: e.target.value }))} />
                                    <datalist id="competitor-names">{names.map(n => <option key={n} value={n} />)}</datalist>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Observation</label>
                                    <textarea className="form-control" rows={4} value={form.observation}
                                        onChange={(e) => setForm(f => ({ ...f, observation: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Category</label>
                                        <input className="form-control" placeholder="pricing, features…" value={form.category}
                                            onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Observed on</label>
                                        <input type="date" className="form-control" value={form.observed_on}
                                            onChange={(e) => setForm(f => ({ ...f, observed_on: e.target.value }))} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Source</label>
                                        <input className="form-control" placeholder="A link, a person, an event" value={form.source}
                                            onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Record</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Competitors;
